/**
 * The one place maplibre-gl is loaded from, and the one place that answers whether it can run.
 *
 * Both are shared because the vector layer is not the only thing that needs them: MapTileLayers
 * warms the bundle up before it knows whether it will draw a vector map at all (see its mounted
 * hook), and Vue2LeafletMaplibre then loads it for real. Two `import()` calls of the same module
 * would be harmless - webpack caches modules - but two copies of the chunk name and two copies of
 * the WebGL probe would not stay in step.
 */

// The in-flight or settled load. Kept at module scope so every caller after the first shares it
// rather than starting its own.
let modulesPromise = null

// Result of the WebGL probe, kept for the life of the page - see hasWebglSupport.
let webglSupported = null

/**
 * maplibre-gl and its Leaflet binding, loaded once.
 *
 * webpackPrefetch injects a <link rel="prefetch"> as soon as the chunk holding the caller loads, so
 * the browser has the bundle (~1.1MB unminified) in cache before anything asks for it. Prefetch is
 * idle-priority and will not compete with the initial page load.
 *
 * @returns {Promise<Array>} the two imported modules, maplibre-gl first
 */
const loadMaplibre = () => {
  if (!modulesPromise) {
    modulesPromise = Promise.all([
      import(/* webpackChunkName: "maplibre-gl", webpackPrefetch: true */ 'maplibre-gl'),
      import(/* webpackChunkName: "maplibre-gl", webpackPrefetch: true */ '@maplibre/maplibre-gl-leaflet')
    ])
  }

  return modulesPromise
}

/**
 * Whether the browser can give MapLibre a context at all.
 *
 * The probe itself takes a real WebGL context, and a browser only hands out a handful per tab
 * before it starts dropping the oldest - which shows up as the map going grey mid-zoom. So the
 * probe hands its context straight back, and the answer is remembered: it cannot change for the
 * life of the page, and asking again is what would cost another context.
 *
 * @returns {boolean}
 */
const hasWebglSupport = () => {
  if (webglSupported !== null) {
    return webglSupported
  }

  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2') || canvas.getContext('webgl')

    context?.getExtension('WEBGL_lose_context')?.loseContext()
    webglSupported = !!context
  }
  catch (error) {
    webglSupported = false
  }

  return webglSupported
}

export {
  loadMaplibre,
  hasWebglSupport
}
